import { useCallback, useState } from 'react';
import { useFetchTextAIRecommendation } from '@api';
import {
  AgeRangeChildOptionValueEnum,
  HorizontalAngleChildOptionValueEnum,
  MainMotionPartChildOptionValueEnum,
  PostureCategoryChildOptionValueEnum,
  StandardFacialExpressionChildOptionValueEnum,
  StandardShotChildOptionValueEnum,
  VerticalAngleChildOptionValueEnum
} from '@constant';
import { useComicActions, useMetaTagActions, useStandardActions } from '@remote-hooks';
import { useSelector } from '@stores';

export const useStandard = () => {
  // 각 항목의 접힘/펼침 상태를 관리하는 state. comic은 query string으로 핸들링
  const [sectionExpandStates, setSectionExpandStates] = useState({
    conti: true,
    tag: true
  });
  const [contiText, setContiText] = useState<string>('');
  const [prevContiText, setPrevContiText] = useState<string>('');

  const isComicSectionExpanded = useSelector((state) => state.comic.isSectionExpanded);
  const isComicToggleOn = useSelector((state) => state.comic.isToggleOn);

  const { onSetStandard } = useStandardActions();
  const { onInitMetaTag, onSetMetaTag } = useMetaTagActions();
  const { onInitComic, onSetComic } = useComicActions();

  const { mutateAsync: fetchTextAIRecommendationMutateAsync, isPending: isFetchTextAIRecommendationLoading } =
    useFetchTextAIRecommendation();

  const handleSidebarRefreshButtonClick = () => {
    // 글콘티 텍스트 삭제
    setContiText('');
    setPrevContiText('');

    // 글콘티 태그 및 관련 태그 삭제
    onInitMetaTag();

    // 글콘티 및 관련 태그 항목 펼치기
    setSectionExpandStates({
      conti: true,
      tag: true
    });

    // 장르 및 작품명 항목 초기화
    onInitComic();
  };

  // 항목 접기/펼치기 함수
  const handleSectionExpand = (sectionName: 'conti' | 'tag' | 'comic') => {
    if (sectionName === 'comic') {
      onSetComic({ isSectionExpanded: !isComicSectionExpanded });
    } else {
      setSectionExpandStates((prev) => ({
        ...prev,
        [sectionName]: !prev[sectionName]
      }));
    }
  };

  // contiText 변경 핸들러 메모이제이션
  const handleContiTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setContiText(e.target.value);
    },
    [setContiText]
  );

  const handleContiSearchButtonClick = async () => {
    const trimmedContiText = contiText.trim();

    // 기존 요청보낸 텍스트와 현재 텍스트가 같다면 요청 전송 방지
    if (trimmedContiText === prevContiText.trim()) return;

    await fetchTextAIRecommendationMutateAsync({ text: trimmedContiText, parentOptionId: 2 }).then((res) => {
      setPrevContiText(contiText);

      const fetchedTextAIRecommendationData = res.data.data;
      const { imageOptionValue, tags } = fetchedTextAIRecommendationData;

      const isBackgroundCutTrue =
        Object.prototype.hasOwnProperty.call(imageOptionValue, '배경컷') && (imageOptionValue['배경컷'] as boolean);
      const isCharacterNumberOne =
        imageOptionValue['남성 인원수'] != null &&
        imageOptionValue['여성 인원수'] != null &&
        typeof imageOptionValue['남성 인원수'] === 'number' &&
        typeof imageOptionValue['여성 인원수'] === 'number' &&
        imageOptionValue['남성 인원수'] + imageOptionValue['여성 인원수'] === 1;

      onSetStandard({
        backgroundCut:
          Object.prototype.hasOwnProperty.call(imageOptionValue, '배경컷') &&
          typeof imageOptionValue['배경컷'] === 'boolean'
            ? {
                포함: { name: '포함', value: 'true', isSelected: imageOptionValue['배경컷'] as boolean },
                제외: { name: '제외', value: 'false', isSelected: !imageOptionValue['배경컷'] as boolean }
              }
            : null,
        characterNumber:
          imageOptionValue['남성 인원수'] != null &&
          imageOptionValue['여성 인원수'] != null &&
          !(imageOptionValue['남성 인원수'] === 0 && imageOptionValue['여성 인원수'] === 0) &&
          !isBackgroundCutTrue
            ? {
                maleNumber: imageOptionValue['남성 인원수'],
                femaleNumber: imageOptionValue['여성 인원수'],
                isWholeGenderNumberChecked: false
              }
            : null,
        shot:
          imageOptionValue['샷 구분'] &&
          imageOptionValue['샷 구분'].length > 0 &&
          imageOptionValue['샷 구분'][0].length > 0
            ? {
                풀샷: {
                  name: '풀샷',
                  value: StandardShotChildOptionValueEnum['풀샷'],
                  isSelected: imageOptionValue['샷 구분'].includes(StandardShotChildOptionValueEnum['풀샷'])
                },
                미디엄샷: {
                  name: '미디엄샷',
                  value: StandardShotChildOptionValueEnum['미디엄샷'],
                  isSelected: imageOptionValue['샷 구분'].includes(StandardShotChildOptionValueEnum['미디엄샷'])
                },
                클로즈업: {
                  name: '클로즈업',
                  value: StandardShotChildOptionValueEnum['클로즈업'],
                  isSelected: imageOptionValue['샷 구분'].includes(StandardShotChildOptionValueEnum['클로즈업'])
                },
                롱샷: {
                  name: '롱샷',
                  value: StandardShotChildOptionValueEnum['롱샷'],
                  isSelected: imageOptionValue['샷 구분'].includes(StandardShotChildOptionValueEnum['롱샷'])
                }
              }
            : null,
        directed:
          Object.prototype.hasOwnProperty.call(imageOptionValue, '연출') &&
          typeof imageOptionValue['연출'] === 'boolean'
            ? {
                포함: { name: '포함', value: 'true', isSelected: imageOptionValue['연출'] as boolean },
                제외: { name: '제외', value: 'false', isSelected: !imageOptionValue['연출'] as boolean }
              }
            : null,
        verticalAngle:
          imageOptionValue['세로앵글'] &&
          imageOptionValue['세로앵글'].length > 0 &&
          imageOptionValue['세로앵글'][0].length > 0
            ? {
                로우앵글: {
                  name: '로우앵글',
                  value: VerticalAngleChildOptionValueEnum['로우앵글'],
                  isSelected: imageOptionValue['세로앵글'].includes(VerticalAngleChildOptionValueEnum['로우앵글'])
                },
                하이앵글: {
                  name: '하이앵글',
                  value: VerticalAngleChildOptionValueEnum['하이앵글'],
                  isSelected: imageOptionValue['세로앵글'].includes(VerticalAngleChildOptionValueEnum['하이앵글'])
                },
                항공샷: {
                  name: '항공샷',
                  value: VerticalAngleChildOptionValueEnum['항공샷'],
                  isSelected: imageOptionValue['세로앵글'].includes(VerticalAngleChildOptionValueEnum['항공샷'])
                },
                평시각: {
                  name: '평시각',
                  value: VerticalAngleChildOptionValueEnum['평시각'],
                  isSelected: imageOptionValue['세로앵글'].includes(VerticalAngleChildOptionValueEnum['평시각'])
                }
              }
            : null,
        ageRange:
          imageOptionValue['연령대'] &&
          imageOptionValue['연령대'].length > 0 &&
          imageOptionValue['연령대'][0].length > 0 &&
          !isBackgroundCutTrue
            ? {
                유아: {
                  name: '유아',
                  value: AgeRangeChildOptionValueEnum['유아'],
                  isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['유아'])
                },
                청소년: {
                  name: '청소년',
                  value: AgeRangeChildOptionValueEnum['청소년'],
                  isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['청소년'])
                },
                청년: {
                  name: '청년',
                  value: AgeRangeChildOptionValueEnum['청년'],
                  isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['청년'])
                },
                중장년: {
                  name: '중장년',
                  value: AgeRangeChildOptionValueEnum['중장년'],
                  isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['중장년'])
                },
                노인: {
                  name: '노인',
                  value: AgeRangeChildOptionValueEnum['노인'],
                  isSelected: imageOptionValue.연령대.includes(AgeRangeChildOptionValueEnum['노인'])
                }
              }
            : null,
        mainMotionPart:
          imageOptionValue['중심동작부위'] &&
          imageOptionValue['중심동작부위'].length > 0 &&
          imageOptionValue['중심동작부위'][0].length > 0 &&
          !isBackgroundCutTrue
            ? {
                손: {
                  name: '손',
                  value: MainMotionPartChildOptionValueEnum['손'],
                  isSelected: imageOptionValue.중심동작부위.includes(MainMotionPartChildOptionValueEnum['손'])
                },
                팔: {
                  name: '팔',
                  value: MainMotionPartChildOptionValueEnum['팔'],
                  isSelected: imageOptionValue.중심동작부위.includes(MainMotionPartChildOptionValueEnum['팔'])
                },
                다리: {
                  name: '다리',
                  value: MainMotionPartChildOptionValueEnum['다리'],
                  isSelected: imageOptionValue.중심동작부위.includes(MainMotionPartChildOptionValueEnum['다리'])
                },
                발: {
                  name: '발',
                  value: MainMotionPartChildOptionValueEnum['발'],
                  isSelected: imageOptionValue.중심동작부위.includes(MainMotionPartChildOptionValueEnum['발'])
                },
                머리: {
                  name: '머리',
                  value: MainMotionPartChildOptionValueEnum['머리'],
                  isSelected: imageOptionValue.중심동작부위.includes(MainMotionPartChildOptionValueEnum['머리'])
                },
                전신: {
                  name: '전신',
                  value: MainMotionPartChildOptionValueEnum['전신'],
                  isSelected: imageOptionValue.중심동작부위.includes(MainMotionPartChildOptionValueEnum['전신'])
                },
                상체: {
                  name: '상체',
                  value: MainMotionPartChildOptionValueEnum['상체'],
                  isSelected: imageOptionValue.중심동작부위.includes(MainMotionPartChildOptionValueEnum['상체'])
                },
                하체: {
                  name: '하체',
                  value: MainMotionPartChildOptionValueEnum['하체'],
                  isSelected: imageOptionValue.중심동작부위.includes(MainMotionPartChildOptionValueEnum['하체'])
                }
              }
            : null,
        facialExpression:
          imageOptionValue['표정'] &&
          imageOptionValue['표정'].length > 0 &&
          imageOptionValue['표정'][0].length > 0 &&
          !isBackgroundCutTrue
            ? {
                없음: {
                  name: '없음',
                  value: StandardFacialExpressionChildOptionValueEnum['없음'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['없음'])
                },
                만족: {
                  name: '만족',
                  value: StandardFacialExpressionChildOptionValueEnum['만족'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['만족'])
                },
                미소: {
                  name: '미소',
                  value: StandardFacialExpressionChildOptionValueEnum['미소'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['미소'])
                },
                새침: {
                  name: '새침',
                  value: StandardFacialExpressionChildOptionValueEnum['새침'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['새침'])
                },
                기쁨: {
                  name: '기쁨',
                  value: StandardFacialExpressionChildOptionValueEnum['기쁨'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['기쁨'])
                },
                폭소: {
                  name: '폭소',
                  value: StandardFacialExpressionChildOptionValueEnum['폭소'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['폭소'])
                },
                수줍음: {
                  name: '수줍음',
                  value: StandardFacialExpressionChildOptionValueEnum['수줍음'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['수줍음'])
                },
                무표정: {
                  name: '무표정',
                  value: StandardFacialExpressionChildOptionValueEnum['무표정'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['무표정'])
                },
                놀람: {
                  name: '놀람',
                  value: StandardFacialExpressionChildOptionValueEnum['놀람'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['놀람'])
                },
                고민: {
                  name: '고민',
                  value: StandardFacialExpressionChildOptionValueEnum['고민'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['고민'])
                },
                진지: {
                  name: '진지',
                  value: StandardFacialExpressionChildOptionValueEnum['진지'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['진지'])
                },
                혐오: {
                  name: '혐오',
                  value: StandardFacialExpressionChildOptionValueEnum['혐오'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['혐오'])
                },
                분노: {
                  name: '분노',
                  value: StandardFacialExpressionChildOptionValueEnum['분노'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['분노'])
                },
                당황: {
                  name: '당황',
                  value: StandardFacialExpressionChildOptionValueEnum['당황'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['당황'])
                },
                공포: {
                  name: '공포',
                  value: StandardFacialExpressionChildOptionValueEnum['공포'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['공포'])
                },
                슬픔: {
                  name: '슬픔',
                  value: StandardFacialExpressionChildOptionValueEnum['슬픔'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['슬픔'])
                },
                경계: {
                  name: '경계',
                  value: StandardFacialExpressionChildOptionValueEnum['경계'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['경계'])
                },
                좌절: {
                  name: '좌절',
                  value: StandardFacialExpressionChildOptionValueEnum['좌절'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['좌절'])
                },
                뻔뻔: {
                  name: '뻔뻔',
                  value: StandardFacialExpressionChildOptionValueEnum['뻔뻔'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['뻔뻔'])
                },
                수치: {
                  name: '수치',
                  value: StandardFacialExpressionChildOptionValueEnum['수치'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['수치'])
                },
                의심: {
                  name: '의심',
                  value: StandardFacialExpressionChildOptionValueEnum['의심'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['의심'])
                },
                불안: {
                  name: '불안',
                  value: StandardFacialExpressionChildOptionValueEnum['불안'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['불안'])
                },
                절규: {
                  name: '절규',
                  value: StandardFacialExpressionChildOptionValueEnum['절규'],
                  isSelected: imageOptionValue.표정.includes(StandardFacialExpressionChildOptionValueEnum['절규'])
                }
              }
            : null,
        deforme:
          Object.prototype.hasOwnProperty.call(imageOptionValue, '데포르메 여부') &&
          typeof imageOptionValue['데포르메 여부'] === 'boolean' &&
          !isBackgroundCutTrue
            ? {
                포함: {
                  name: '포함',
                  value: 'true',
                  isSelected: imageOptionValue['데포르메 여부'] as boolean
                },
                제외: {
                  name: '제외',
                  value: 'false',
                  isSelected: !imageOptionValue['데포르메 여부'] as boolean
                }
              }
            : null,
        horizontalAngle:
          imageOptionValue['가로앵글'] &&
          imageOptionValue['가로앵글'].length > 0 &&
          imageOptionValue['가로앵글'][0].length > 0 &&
          !isBackgroundCutTrue
            ? {
                정면: {
                  name: '정면',
                  value: HorizontalAngleChildOptionValueEnum['정면'],
                  isSelected: imageOptionValue['가로앵글'].includes(HorizontalAngleChildOptionValueEnum['정면'])
                },
                정측면: {
                  name: '정측면',
                  value: HorizontalAngleChildOptionValueEnum['정측면'],
                  isSelected: imageOptionValue['가로앵글'].includes(HorizontalAngleChildOptionValueEnum['정측면'])
                },
                측면: {
                  name: '측면',
                  value: HorizontalAngleChildOptionValueEnum['측면'],
                  isSelected: imageOptionValue['가로앵글'].includes(HorizontalAngleChildOptionValueEnum['측면'])
                },
                후측면: {
                  name: '후측면',
                  value: HorizontalAngleChildOptionValueEnum['후측면'],
                  isSelected: imageOptionValue['가로앵글'].includes(HorizontalAngleChildOptionValueEnum['후측면'])
                },
                후면: {
                  name: '후면',
                  value: HorizontalAngleChildOptionValueEnum['후면'],
                  isSelected: imageOptionValue['가로앵글'].includes(HorizontalAngleChildOptionValueEnum['후면'])
                }
              }
            : null,
        postureCategory:
          imageOptionValue['동작구분'] &&
          imageOptionValue['동작구분'].length > 0 &&
          imageOptionValue['동작구분'][0].length > 0 &&
          !isBackgroundCutTrue
            ? {
                없음: {
                  name: '없음',
                  value: PostureCategoryChildOptionValueEnum['없음'],
                  isSelected: imageOptionValue['동작구분'].includes(PostureCategoryChildOptionValueEnum['없음'])
                },
                서있음: {
                  name: '서있음',
                  value: PostureCategoryChildOptionValueEnum['서있음'],
                  isSelected: imageOptionValue['동작구분'].includes(PostureCategoryChildOptionValueEnum['서있음'])
                },
                앉음: {
                  name: '앉음',
                  value: PostureCategoryChildOptionValueEnum['앉음'],
                  isSelected: imageOptionValue['동작구분'].includes(PostureCategoryChildOptionValueEnum['앉음'])
                },
                누움: {
                  name: '누움',
                  value: PostureCategoryChildOptionValueEnum['누움'],
                  isSelected: imageOptionValue['동작구분'].includes(PostureCategoryChildOptionValueEnum['누움'])
                },
                엎드림: {
                  name: '엎드림',
                  value: PostureCategoryChildOptionValueEnum['엎드림'],
                  isSelected: imageOptionValue['동작구분'].includes(PostureCategoryChildOptionValueEnum['엎드림'])
                }
              }
            : null,
        skinship:
          Object.prototype.hasOwnProperty.call(imageOptionValue, '스킨십') &&
          typeof imageOptionValue['스킨십'] === 'boolean' &&
          !isBackgroundCutTrue &&
          !isCharacterNumberOne
            ? {
                포함: {
                  name: '포함',
                  value: 'true',
                  isSelected: imageOptionValue['스킨십'] as boolean
                },
                제외: {
                  name: '제외',
                  value: 'false',
                  isSelected: !imageOptionValue['스킨십'] as boolean
                }
              }
            : null
      });

      onSetMetaTag({ aiRecommendedMetaTags: tags.map((tag) => tag) });
    });
  };

  const handleComicSearchToggle = () => {
    // 작품 정보로 검색 항목이 접혀있고 스위치가 OFF일 시 스위치 ON으로 변경하면 항목 펼치기
    if (!isComicSectionExpanded && !isComicToggleOn) {
      onSetComic({ isSectionExpanded: true });
    }

    onSetComic({ isToggleOn: !isComicToggleOn });
  };

  return {
    handleSidebarRefreshButtonClick,

    sectionExpandStates,
    handleSectionExpand,

    contiText,
    handleContiTextChange,
    isFetchTextAIRecommendationLoading,
    handleContiSearchButtonClick,

    handleComicSearchToggle,
    isComicSectionExpanded,
    isComicToggleOn
  };
};
